import { useEffect, useState } from 'react';

import { apiPath, fetchBlobURL } from '@/shared/api/binary';

export interface AuthImageProps {
  /**
   * Путь вида `/media/{id}/thumb` — или подписанный адрес превью из
   * `thumb_url` (`…/preview?…&sig=…`), которому токен не нужен.
   */
  src: string;
  alt: string;
  className?: string | undefined;
}

interface State {
  /** Для какого адреса состояние: по нему видно, что оно устарело. */
  src: string;
  url?: string;
  failed: boolean;
}

/**
 * Картинка из медиатеки.
 *
 * Медиатека не публична: файл отдаётся с тем же токеном, что и остальное API,
 * а `<img src>` заголовок Authorization поставить не умеет. Поэтому
 * содержимое забирается запросом и показывается по blob-ссылке.
 *
 * Ссылка освобождается при уходе компонента: без этого каждая пролистанная
 * страница медиатеки оставляла бы картинки в памяти вкладки навсегда.
 */
export function AuthImage({ src, alt, className }: AuthImageProps) {
  // Подписанный адрес — обычная картинка: браузер берёт её из кеша, а не
  // скачивает заново при каждом заходе, и грузит, только когда видна.
  if (src.includes('sig=')) {
    return <SignedImage src={src} alt={alt} className={className} />;
  }

  return <BlobImage src={src} alt={alt} className={className} />;
}

function SignedImage({ src, alt, className }: AuthImageProps) {
  const [failed, setFailed] = useState<string>();

  if (failed === src) {
    return <span className={className} aria-label={alt} role="img" />;
  }

  return (
    <img
      className={className}
      src={apiPath(src)}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => {
        setFailed(src);
      }}
    />
  );
}

function BlobImage({ src, alt, className }: AuthImageProps) {
  const [state, setState] = useState<State>({ src, failed: false });

  // Смена адреса сбрасывает показанное прямо в теле рендера, а не эффектом:
  // так старая картинка не остаётся на экране лишний проход отрисовки.
  if (state.src !== src) {
    setState({ src, failed: false });
  }

  useEffect(() => {
    const abort = new AbortController();
    let created: string | undefined;

    fetchBlobURL(src, abort.signal)
      .then((value) => {
        created = value;
        setState({ src, url: value, failed: false });
      })
      .catch(() => {
        // Прерванный запрос — это уход со страницы, а не поломка. Отличать их
        // здесь незачем: показать всё равно нечего.
        if (!abort.signal.aborted) {
          setState({ src, failed: true });
        }
      });

    return () => {
      abort.abort();

      if (created !== undefined) {
        URL.revokeObjectURL(created);
      }
    };
  }, [src]);

  if (state.failed || state.url === undefined) {
    // Пустое место того же размера, а не подпрыгивающая разметка: плитки
    // выстроены в сетку, и пропавшая картинка не должна её ломать.
    return <span className={className} aria-label={alt} role="img" />;
  }

  return <img className={className} src={state.url} alt={alt} />;
}
